import type { ApolloCache, FetchResult } from '@apollo/client';
import { cloneDeep } from '@apollo/client/utilities';

import { client as apolloClient } from 'src/shared/graphql';
import { AiOriginalImagesDocument } from 'src/shared/graphql/generated';
import type {
  AiOriginalImagesQuery,
  AiOriginalImagesQueryVariables,
  CreateAiOriginalImageMutation,
} from 'src/shared/graphql/generated';
import { mutations } from 'src/shared/graphql/requests';
import {
  createMd5Hash,
  uploadFileDirectly,
} from 'src/shared/helpers/fileUpload';
import { logError } from 'src/shared/telemetry/logger';

interface CreateAiOriginalImageConfig {
  spaceType?: string;
  sessionId?: string;
  email?: string;
}

const aiOriginalImageCacheUpdater = (
  cache: ApolloCache<any>,
  mutationResult: Omit<
    FetchResult<
      CreateAiOriginalImageMutation,
      Record<string, any>,
      Record<string, any>
    >,
    'context'
  >,
) => {
  cache.updateQuery<AiOriginalImagesQuery, AiOriginalImagesQueryVariables>(
    {
      query: AiOriginalImagesDocument,
    },
    prevData => {
      if (!prevData || !mutationResult.data?.createAiOriginalImage) {
        return undefined;
      }

      const newOriginalImage = mutationResult.data?.createAiOriginalImage;
      const nextData = cloneDeep(prevData);
      nextData.aiOriginalImages?.push({
        ...newOriginalImage,
        aiGeneratedImages: [],
      });
      return nextData;
    },
  );
};

const createAiOriginalImage = async (
  file: File,
  uploadConfig: CreateAiOriginalImageConfig = {},
) => {
  const { sessionId = '', email = '', spaceType = '' } = uploadConfig;
  const checksum = await createMd5Hash(file);
  const createPlaceholderVariables = {
    byteSize: file.size,
    filename: file.name,
    contentType: file.type,
    checksum,
    email,
    sessionId,
    spaceType,
  };

  const { data: createPlaceholderData, errors: createPlaceholderErrors } =
    await mutations.createAiOriginalImagePlaceholder(
      apolloClient,
      createPlaceholderVariables,
    );
  if (createPlaceholderErrors?.length) {
    createPlaceholderErrors.forEach(error => {
      logError(error);
    });
    return null;
  }

  const uploadUrl =
    createPlaceholderData?.createAiOriginalImagePlaceholder.uploadUrl ?? '';
  const uploadHeaders =
    createPlaceholderData?.createAiOriginalImagePlaceholder.uploadHeaders;
  const imageSignedId =
    createPlaceholderData?.createAiOriginalImagePlaceholder.signedId ?? '';
  const aiOriginalImageId = String(
    createPlaceholderData?.createAiOriginalImagePlaceholder.aiOriginalImageId,
  );

  const gcpResponse = await uploadFileDirectly({
    file,
    uploadUrl,
    uploadHeaders: uploadHeaders ?? undefined,
    stripContentType: true,
  });
  if (gcpResponse.ok) {
    const {
      errors: createAiOriginalImageErrors,
      data: createAiOriginalImageData,
    } = await mutations.createAiOriginalImage(
      apolloClient,
      {
        aiOriginalImageId,
        imageSignedId,
        agreedToTerms: true,
        imageFlexibility: 0,
        intensity: 0,
        style: '',
      },
      aiOriginalImageCacheUpdater,
    );
    if (createAiOriginalImageErrors?.length) {
      createAiOriginalImageErrors.forEach(error => {
        logError(error);
      });
      return null;
    }
    return createAiOriginalImageData;
  }
  return gcpResponse;
};

export { createAiOriginalImage };
